import type {ReactNode} from "react";
import {createContext, useContext, useEffect, useRef, useState} from "react";
import type {Root} from "../../cdx-models/Root";
import type {AnyModel, WithAllFieldsLoaded} from "../../cdx-models/utils/MakeModel";
import type {ModelOverview} from "../../cdx-models";
import type {api as apiInstance} from "../api";

export type MateApi = typeof apiInstance;

const MateContext = createContext<MateApi>(null as unknown as MateApi);

export const MateContextProvider = ({children, api}: {children: ReactNode; api: MateApi}) => {
  return <MateContext.Provider value={api}>{children}</MateContext.Provider>;
};

export const useRoot = () => {
  const api = useContext(MateContext);
  const [root, setRoot] = useState<Root>(api.getRoot);
  useEffect(() => api.registerOnChangeListener(() => setRoot(api.getRoot())), [api]);
  return root;
};

export const useApi = () => useContext(MateContext);

export const useInstance = <T extends keyof ModelOverview>(
  modelName: T,
  id: any
): ModelOverview[T] | null => {
  const api = useContext(MateContext);
  const [, setCounter] = useState(0);
  const modelRef = useRef<any>(null);
  modelRef.current = id && api.getModel({modelName, id});
  useEffect(
    () =>
      api.registerOnChangeListener(() => {
        const model = id && api.getModel({modelName, id});
        if (model !== modelRef.current) setCounter((c) => c + 1);
      }),
    [modelName, id, api]
  );
  return modelRef.current && !modelRef.current.$meta.isDeleted() ? modelRef.current : null;
};

export const checkIfPresent = <T extends any>(
  getDataFn: () => T,
  api: MateApi
  // debug?: boolean
): {isLoaded: boolean; result: T} => {
  let isLoaded = true;
  const unsub = api.registerOnNotLoadedListener((status: string) => {
    if (status === "loading" || status === "notLoaded") {
      // if (debug) debugger;
      isLoaded = false;
    }
  });
  const result = getDataFn();
  unsub();
  return {isLoaded, result};
};

export const withLoadedData = <TIn extends AnyModel, TOut>(
  api: MateApi,
  instance: TIn,
  getDataFn: (loadedInstance: WithAllFieldsLoaded<TIn>) => TOut
) => {
  let isLoaded = true;
  const unsub = api.registerOnNotLoadedListener((status: string) => {
    if (status === "loading" || status === "notLoaded") {
      isLoaded = false;
    }
  });
  const result = getDataFn(instance as any);
  unsub();
  return isLoaded ? result : null;
};

export const checkIfValid = <T extends any>(
  getDataFn: () => T,
  api: MateApi
): {isLoaded: boolean; result: T} => {
  let isLoaded = true;
  const unsub = api.registerOnNotLoadedListener((_status: string) => {
    isLoaded = false;
  });
  const result = getDataFn();
  unsub();
  return {isLoaded, result};
};
